<script lang="ts" setup>
import { computed, onMounted, Ref, ref } from 'vue';
import { storeToRefs } from 'pinia';
import Chip from 'primevue/chip';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import { useToast } from 'primevue/usetoast';
import { useStudentCaseManagerStore } from '@/store';
import { PERMISSIONS } from '@/enum';
import { can } from '@/composables';

interface CaseManagerPropsInterface {
  studentId?: number;
}

interface CaseManagerTableInterface {
  id: number;
  display_name: string;
}

const props = defineProps<CaseManagerPropsInterface>();
const studentCaseManagerStore = useStudentCaseManagerStore();
const { caseManagersForStudentSelected: caseManagers, loading } = storeToRefs(studentCaseManagerStore);
const caseManagerCount: Ref<number> = computed(() => (caseManagers.value ? caseManagers.value.length : 0));
const caseManagerModalOpen = ref(false);
const addCaseManagerDataLoading = ref(false);
const toast = useToast();
const caseManagerItems: Ref<{ name: string; value: number }[]> = ref([]);

const refreshCaseManagers = () => studentCaseManagerStore.getCaseManagersForStudent(props.studentId as number);

onMounted(() => {
  if (can([PERMISSIONS.STUDENT_CASE_MANAGERS_INDEX])) refreshCaseManagers();
});

const createMenuModel = (item: CaseManagerTableInterface) => {
  return [
    {
      label: 'Remove Case Manager',
      command: () => {
        detachCaseManager([item.id], item.display_name);
      },
      visible: can([PERMISSIONS.STUDENT_DETACH_CASE_MANAGERS]),
    },
  ].filter((item) => item.visible);
};

const attachCaseManagers: (ids: number[]) => Promise<void> = async (ids) => {
  await studentCaseManagerStore.attachCaseManagers({ caseManagers: ids, studentId: props.studentId as number });
  await refreshCaseManagers();
  toast.add({
    severity: 'success',
    summary: 'Added',
    detail: 'Case Manager(s) has been added',
    life: 3000,
  });
};

const detachCaseManager: (ids: number[], caseManagerName: string) => Promise<void> = async (ids, caseManagerName) => {
  await studentCaseManagerStore.detachCaseManagers({ caseManagers: ids, studentId: props.studentId as number });
  await refreshCaseManagers();
  toast.add({
    severity: 'success',
    summary: 'Removed',
    detail: `Case Manager ${caseManagerName} has been removed from student`,
    life: 3000,
  });
};

const openAddCaseManagers = async () => {
  caseManagerItems.value = [];
  if (can([PERMISSIONS.STUDENT_CASE_MANAGERS_AVAILABLE_TO_ADD]) && can([PERMISSIONS.STUDENT_ATTACH_CASE_MANAGERS])) {
    addCaseManagerDataLoading.value = true;
    caseManagerModalOpen.value = true;
    caseManagerItems.value = await studentCaseManagerStore.getAddCaseManagersModalData(props.studentId as number);
    addCaseManagerDataLoading.value = false;
  } else {
    toast.add({
      severity: 'warning',
      summary: 'Error',
      detail: 'You do not have permission to add case managers',
      life: 3000,
    });
  }
};

const onCancelAdd = () => (caseManagerModalOpen.value = false);

const onSaveAdd = (caseManagers: { name: string; value: number }[]) => {
  const caseManagerIds = caseManagers.map((caseManager) => caseManager.value);
  attachCaseManagers(caseManagerIds);
  caseManagerModalOpen.value = false;
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Case Managers</h2>
      <Chip>{{ caseManagerCount }}</Chip>
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.STUDENT_ATTACH_CASE_MANAGERS])"
          type="text"
          size="small"
          text="Add Case Managers"
          icon-left="add"
          @click="openAddCaseManagers"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="caseManagers"
        :global-filter-fields="['display_name']"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="loading"
        data-key="id"
        responsive-layout="scroll"
        scroll-height="440px"
        state-key="session-store-student-case-managers-table"
        state-storage="session"
        :class="{ 'table-loading': loading }"
      >
        <template v-if="caseManagerCount">
          <Column
            body-class="mw-20rem"
            header-class="mw-20rem"
            field="display_name"
            header="Case Manager Name"
            sortable
          >
            <template #body="{ data }">
              <a class="font-semibold" :href="`/user/${data.id}`" v-text="data.display_name" />
            </template>
          </Column>
          <Column />
          <Column
            body-class="justify-content-center mw-10rem"
            header-class="justify-content-center mw-10rem"
            field="id"
            header="Actions"
          >
            <template #body="{ data }">
              <ActionMenu
                v-if="createMenuModel(data).length > 0"
                :menu-items="createMenuModel(data)"
                icon="more_horiz"
              />
            </template>
          </Column>
        </template>
        <template #empty>Case Managers not assigned</template>
      </DataTable>
      <AddPersonModal
        v-if="caseManagerModalOpen && can([PERMISSIONS.STUDENT_CASE_MANAGERS_AVAILABLE_TO_ADD])"
        title="Add Case Managers"
        button-text="Add Case Managers"
        placeholder="Search case managers"
        :loading="addCaseManagerDataLoading"
        :items="caseManagerItems"
        :searchable="true"
        @add-items="onSaveAdd"
        @cancel="onCancelAdd"
      />
    </template>
  </Card>
</template>
